import {format} from 'date-fns';
import {
    calendar,
    timeInfo
} from '@dazn/lp-web/src/constants/resourceStrings.constants';
import {getDictionaryString} from '@dazn/lp-web/src/utils/dictionary';
import {getLanguage} from '@dazn/lp-web/src/utils/bootstrap';
import {ENGLISH} from '@dazn/lp-web/src/constants/language.constants';

const checkShouldShowDateTimeBadge = () => {
    try {
        return window.dazn.features.isEnabledAnonymous(
            'f_ar_catalog_tile_redesign',
            'ShowDateTimeBadge'
        );
    } catch (e) {
        return false;
    }
};

const appLanguage = getLanguage();

const getDayNumber = (date: Date) => {
    const dayFormat = getDictionaryString('catalog_day_format');
    return format(date, dayFormat);
};

const getCalendarUnit = (
    date: Date,
    getDictionaryString: (key: string) => string,
    dateFormat: string,
    suffix: string
) => {
    const dateNfsFormat = convertToDateNfsFormat(dateFormat);
    const unit = format(date, dateNfsFormat);
    const key = (calendar as any)[`${unit.toUpperCase()}_${suffix}`];
    return getDictionaryString(key);
};

const getDayNumberOrdinal = (date: Date, language: string) => {
    const specialFormatting: any = {
        [ENGLISH]: 'Do'
    };
    const dayFormat = specialFormatting[language] || 'D';
    return format(date, dayFormat);
};

const getDayNumberUnpadded = (date: Date) => {
    return format(date, 'D');
};

const getTime = (date: Date, getDictionaryString: (key: string) => string) => {
    const timeFormat = convertToDateNfsFormat(
        checkShouldShowDateTimeBadge()
            ? getDictionaryString(timeInfo.DATE_12_HOUR_FORMAT)
            : getDictionaryString(timeInfo.DATE_ISO_TIME)
    );
    return format(date, timeFormat);
};

const getMonthNumber = (date: Date) => {
    const monthFormat = getDictionaryString('catalog_month_format');
    return format(date, monthFormat);
};

const getMonthNumberUnpadded = (date: Date) => {
    return format(date, 'M');
};
const getYear = (date: Date) => {
    return format(date, 'YYYY');
};

const dateFragmentMapper =
    (language: string, getDictionaryString: (key: string) => string) =>
    (date: Date) => ({
        dayOfWeekLong: getCalendarUnit(
            date,
            getDictionaryString,
            'dddd',
            'LONG'
        ),
        dayOfWeekLongUppercase: getCalendarUnit(
            date,
            getDictionaryString,
            'dddd',
            'LONG_UPPERCASE'
        ),
        dayOfWeekLongPropercase: getCalendarUnit(
            date,
            getDictionaryString,
            'dddd',
            'LONG_PROPERCASE'
        ),
        dayOfWeekShort: getCalendarUnit(
            date,
            getDictionaryString,
            'dddd',
            'SHORT'
        ),
        lastDayOfWeekLong: getCalendarUnit(
            date,
            getDictionaryString,
            'dddd',
            'LONG_LAST'
        ),
        dayOfWeekShortUppercase: getCalendarUnit(
            date,
            getDictionaryString,
            'dddd',
            'SHORT_UPPERCASE'
        ),
        dayOfWeekShortPropercase: getCalendarUnit(
            date,
            getDictionaryString,
            'dddd',
            'SHORT_PROPERCASE'
        ), // eslint-disable-line max-len
        dayOfMonth: getDayNumber(date),
        dayOfMonthOrdinal: getDayNumberOrdinal(date, language),
        monthLong: getCalendarUnit(date, getDictionaryString, 'MMMM', 'LONG'),
        monthLongUppercase: getCalendarUnit(
            date,
            getDictionaryString,
            'MMMM',
            'LONG_UPPERCASE'
        ),
        monthLongPropercase: getCalendarUnit(
            date,
            getDictionaryString,
            'MMMM',
            'LONG_PROPERCASE'
        ),
        monthShort: getCalendarUnit(date, getDictionaryString, 'MMMM', 'SHORT'),
        monthShortUppercase: checkShouldShowDateTimeBadge()
            ? getCalendarUnit(
                  date,
                  getDictionaryString,
                  'MMMM',
                  'SHORT_UPPERCASE'
              ).slice(0, 3)
            : getCalendarUnit(
                  date,
                  getDictionaryString,
                  'MMMM',
                  'SHORT_UPPERCASE'
              ),
        monthShortPropercase: getCalendarUnit(
            date,
            getDictionaryString,
            'MMMM',
            'SHORT_PROPERCASE'
        ),
        monthNumber: getMonthNumber(date),
        time: getTime(date, getDictionaryString),
        year: getYear(date)
    });

const convertToDateNfsFormat = (dateFormat: string) => {
    return dateFormat
        .replace(/([^']*'[^']*)'/g, '$1]') // Replace Odd Occurence of a character
        .replace(/'/g, '[');
};

const getDateFragments = dateFragmentMapper(appLanguage, getDictionaryString);

export {
    getDateFragments as default,
    getDayNumber,
    getDayNumberUnpadded,
    dateFragmentMapper,
    getCalendarUnit,
    getDayNumberOrdinal,
    getMonthNumber,
    getMonthNumberUnpadded,
    getTime,
    convertToDateNfsFormat
};
